
import { Component, Prop, Vue } from 'vue-property-decorator';
import { MerchantConfig } from '@/models/config/merchantConfig';
import { CustomerInfo } from '@/models/customers/customerinfo';
import { customerService } from '@/services/customers/customer.service';
import {
  mobileAppService,
  MobileAppRegistrationRequest,
} from '@/services/mobileapp/mobileapp.service';
import { urlQueryService } from '@/services/common/urlquery.service';
import { Routes } from '@/router/routes';
import { MediasGetters } from '@/store/modules/medias/enums';
import { MediaStatus } from '@/models/medias/mediaStatus';
import { CommonGetters } from '@/store/modules/common/enums';
import { isNotNullOrEmpty } from '@glitchedmob/isnullorempty';
import FormField from './form/FormField.vue';
import PasswordInput from './form/PasswordInput.vue';
import PasswordRequirements from './form/PasswordRequirements.vue';
import PrivacyConsent from './form/PrivacyConsent.vue';
import PrivacyRadioGroup from './form/PrivacyRadioGroup.vue';
import { FormValidator, ValidationState } from '@/utils/form-validator';
import { emailVerificationService } from '@/services/mobileapp/email-verification.service';
import { BModal } from 'bootstrap-vue';

const PW_SPECIAL_CHARS_LABEL =
  '@ # $ % ^ & * - _ + = [ ] { } | \\ : \' , ? / ` ~ " ( ) ; .';

const STORAGE_KEY = 'registration_form_draft';
const VERIFIED_EMAIL_KEY = 'registration_verified_email';
const STORAGE_TTL_MS = 5 * 60 * 1000; // 5 minutes

type RegistrationStep = 'verify-email' | 'register';

interface RegistrationForm {
  name: string;
  surname: string;
  email: string;
  password: string;
  confirmPassword: string;
  privacy: boolean;
  privacy1: boolean;
  privacy2: boolean;
  privacy3: boolean;
  mediaHash?: string;
  mobileHash?: string;
  merchantId: string;
  mediaType: number;
  mediaHwId: string;
  mediaSerial: string;
  language: string;
}

type ValidatableField =
  | 'name'
  | 'surname'
  | 'email'
  | 'password'
  | 'confirmPassword'
  | 'privacy';

@Component({
  components: {
    'ck-form-field': FormField,
    'ck-password-input': PasswordInput,
    'ck-password-requirements': PasswordRequirements,
    'ck-privacy-consent': PrivacyConsent,
    'ck-privacy-radio-group': PrivacyRadioGroup,
  },
})
export default class AuthPanelRegister extends Vue {
  @Prop({ required: true }) merchantConfig!: MerchantConfig;

  readonly PW_SPECIAL_CHARS_LABEL = PW_SPECIAL_CHARS_LABEL;

  mobileAppSignupSuccess = false;
  currentStep: RegistrationStep = 'verify-email';

  otpSent = false;
  otpCode = '';
  otpError = '';
  isSendingOtp = false;
  isVerifying = false;
  resendCooldown = 0;
  private resendTimer: number | null = null;

  userInfo: RegistrationForm = this.createInitialFormState();

  validation: Record<ValidatableField, ValidationState> = {
    name: { valid: true, touched: false, error: '' },
    surname: { valid: true, touched: false, error: '' },
    email: { valid: true, touched: false, error: '' },
    password: { valid: true, touched: false, error: '' },
    confirmPassword: { valid: true, touched: false, error: '' },
    privacy: { valid: true, touched: false, error: '' },
  };

  private validator!: FormValidator;

  get hasCustomer(): boolean {
    const media = this.$store.getters[
      MediasGetters.GetMediaStatusToAssociate
    ] as MediaStatus | null;
    return !!media?.customerId;
  }

  get isMobileAppRegistration(): boolean {
    const query = urlQueryService.getMobileAppRegistrationQuery();
    return isNotNullOrEmpty(query?.hash);
  }

  get isFormValid(): boolean {
    const allFieldsValid = Object.values(this.validation).every((v) => v.valid);
    const requiredFieldsFilled = this.checkRequiredFields();
    return allFieldsValid && requiredFieldsFilled;
  }

  created(): void {
    this.validator = new FormValidator(this.$t.bind(this));
  }

  mounted(): void {
    if (!this.isMobileAppRegistration) {
      this.currentStep = 'register';
    } else {
      this.checkVerifiedEmail();
    }
    this.restoreFormData();
  }

  beforeDestroy(): void {
    this.saveFormData();
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }

  private checkVerifiedEmail(): void {
    if (!this.isMobileAppRegistration) {
      return;
    }

    try {
      const saved = sessionStorage.getItem(VERIFIED_EMAIL_KEY);
      if (saved) {
        const data = JSON.parse(saved);

        if (data.verifiedAt && Date.now() - data.verifiedAt > STORAGE_TTL_MS) {
          this.clearVerifiedEmail();
          return;
        }

        this.userInfo.email = data.email;
        this.currentStep = 'register';
      }
    } catch (error) {
      console.warn('Error checking verified email:', error);
    }
  }

  private saveVerifiedEmail(email: string): void {
    try {
      sessionStorage.setItem(
        VERIFIED_EMAIL_KEY,
        JSON.stringify({
          email,
          verifiedAt: Date.now(),
        }),
      );
    } catch (error) {
      console.warn('Error saving verified email:', error);
    }
  }

  private clearVerifiedEmail(): void {
    try {
      sessionStorage.removeItem(VERIFIED_EMAIL_KEY);
    } catch (error) {
      console.warn('Error clearing verified email:', error);
    }
  }

  async handleSendOtp(resetOtpField = false): Promise<void> {
    this.validation.email.touched = true;
    this.validateField('email');

    if (!this.validation.email.valid || !this.userInfo.email) {
      return;
    }

    this.isSendingOtp = true;
    this.otpError = '';
    if (resetOtpField) {
      this.otpCode = '';
    }

    try {
      const result = await emailVerificationService.sendOtp(
        this.userInfo.email,
        this.$store.getters[CommonGetters.Language],
      );

      if (result.success) {
        this.otpSent = true;
        this.startResendCooldown(60);
        this.$toaster.showSuccess('register.verification-code-sent', true);
      } else {
        this.otpError =
          result.error || (this.$t('register.send-otp-error') as string);
      }
    } catch (error: any) {
      const apiError = error?.response?.data?.error;
      this.validation.email.valid = false;
      this.validation.email.error =
        apiError || (this.$t('register.send-otp-error') as string);
    } finally {
      this.isSendingOtp = false;
    }
  }

  async handleVerifyOtp(): Promise<void> {
    if (this.otpCode.length !== 6) {
      return;
    }

    this.isVerifying = true;
    this.otpError = '';

    try {
      const result = await emailVerificationService.verifyOtp(
        this.userInfo.email,
        this.otpCode,
      );

      if (result.verified) {
        this.saveVerifiedEmail(this.userInfo.email);
        this.currentStep = 'register';
        this.$toaster.showSuccess('register.email-verified-success', true);
      } else {
        this.otpError =
          result.error || (this.$t('register.invalid-code') as string);
      }
    } catch (error) {
      this.otpError = this.$t('register.verify-otp-error') as string;
    } finally {
      this.isVerifying = false;
    }
  }

  resetEmailEntry(): void {
    this.otpSent = false;
    this.otpCode = '';
    this.otpError = '';
    this.userInfo.email = '';

    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendCooldown = 0;
    }
  }

  handleChangeEmail(): void {
    (this.$refs['change-email-modal'] as BModal).show();
  }

  confirmChangeEmail(): void {
    this.clearVerifiedEmail();
    this.otpSent = false;
    this.otpCode = '';
    this.otpError = '';
    this.userInfo.email = '';
    this.currentStep = 'verify-email';

    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendCooldown = 0;
    }
  }

  onOtpInput(): void {
    this.otpCode = this.otpCode.replace(/\D/g, '').slice(0, 6);
    this.otpError = '';
  }

  private startResendCooldown(seconds: number): void {
    this.resendCooldown = seconds;

    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }

    this.resendTimer = window.setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0 && this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    }, 1000);
  }

  onPasswordInput(): void {
    this.validateField('password');

    if (this.validation.confirmPassword.touched) {
      this.validateField('confirmPassword');
    }
  }

  onEmailInput(): void {
    this.validateField('email');
  }

  onConfirmPasswordInput(): void {
    this.validateField('confirmPassword');
  }

  onFieldBlur(field: ValidatableField): void {
    this.validation[field].touched = true;
    this.validateField(field);
  }

  onFieldInput(field: ValidatableField): void {
    this.validation[field].touched = true;
    this.validateField(field);
  }

  fieldClass(field: ValidatableField): string {
    const state = this.validation[field];
    return state.touched && !state.valid ? 'is-invalid' : '';
  }

  async handleSubmit(): Promise<void> {
    this.validateAllFields();

    if (!this.isFormValid) {
      return;
    }

    this.$spinner.showSpinner();

    try {
      if (this.isMobileAppRegistration) {
        await this.registerMobileAppUser();
      } else {
        await this.registerCogesKeyUser();
      }
    } catch (error) {
      this.handleRegistrationError(error);
    } finally {
      this.$spinner.removeSpinner();
    }
  }

  private createInitialFormState(): RegistrationForm {
    return {
      name: '',
      surname: '',
      email: '',
      password: '',
      confirmPassword: '',
      privacy: false,
      privacy1: false,
      privacy2: false,
      privacy3: false,
      mediaHash: urlQueryService.getQRCodeQuery()?.hash,
      merchantId: this.merchantConfig.merchantId || '',
      mediaType: 0,
      mediaHwId: '',
      mediaSerial: '',
      language:
        this.$store.getters[CommonGetters.Language]?.toLowerCase() ?? 'en',
    };
  }

  private saveFormData(): void {
    const dataToSave = {
      name: this.userInfo.name,
      surname: this.userInfo.surname,
      email: this.userInfo.email,
      privacy: this.userInfo.privacy,
      privacy1: this.userInfo.privacy1,
      privacy2: this.userInfo.privacy2,
      privacy3: this.userInfo.privacy3,
      savedAt: Date.now(),
    };

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.warn(error);
    }
  }

  private restoreFormData(): void {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);

        if (data.savedAt && Date.now() - data.savedAt > STORAGE_TTL_MS) {
          this.clearFormData();
          return;
        }

        this.userInfo.name = data.name || '';
        this.userInfo.surname = data.surname || '';
        if (!this.isMobileAppRegistration) {
          this.userInfo.email = data.email || '';
        }
        this.userInfo.privacy = data.privacy || false;
        this.userInfo.privacy1 = data.privacy1 || false;
        this.userInfo.privacy2 = data.privacy2 || false;
        this.userInfo.privacy3 = data.privacy3 || false;
      }
    } catch (error) {
      console.warn(error);
    }
  }

  private clearFormData(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn(error);
    }
  }

  private validateField(field: ValidatableField): void {
    const { userInfo, validator } = this;

    const validationRules: Record<ValidatableField, () => ValidationState> = {
      name: () => validator.validateName(userInfo.name),
      surname: () => validator.validateSurname(userInfo.surname),
      email: () => validator.validateEmail(userInfo.email),
      password: () => validator.validatePassword(userInfo.password),
      confirmPassword: () =>
        validator.validateConfirmPassword(
          userInfo.password,
          userInfo.confirmPassword,
        ),
      privacy: () => validator.validatePrivacy(userInfo.privacy),
    };

    const result = validationRules[field]();
    this.validation[field] = {
      ...result,
      touched: this.validation[field].touched,
    };

    if (field === 'password' && this.validation.confirmPassword.touched) {
      this.validateField('confirmPassword');
    }
  }

  private validateAllFields(): void {
    const fields: ValidatableField[] = [
      'name',
      'surname',
      'email',
      'password',
      'confirmPassword',
      'privacy',
    ];

    fields.forEach((field) => {
      this.validation[field].touched = true;
      this.validateField(field);
    });
  }

  private checkRequiredFields(): boolean {
    const { name, surname, email, password, confirmPassword, privacy } =
      this.userInfo;
    return !!(
      name?.trim() &&
      surname?.trim() &&
      email?.trim() &&
      password &&
      confirmPassword &&
      privacy
    );
  }

  private async registerMobileAppUser(): Promise<void> {
    this.userInfo.mobileHash =
      urlQueryService.getMobileAppRegistrationQuery()?.hash;
    const request = this.mapToMobileAppRequest();

    await mobileAppService.registerUser(request);
    this.clearFormData();
    this.clearVerifiedEmail();
    this.mobileAppSignupSuccess = true;
    this.$emit('success');
  }

  private async registerCogesKeyUser(): Promise<void> {
    await customerService.createProfile(
      this.userInfo as unknown as CustomerInfo,
    );
    this.clearFormData();
    this.$toaster.showSuccess('register.registration-success', true);
    this.$router.push({ name: Routes.Home });
  }

  private handleRegistrationError(error: unknown): void {
    const errorMsg = (error as any)?.response?.data?.Detail;
    if (errorMsg) {
      this.$toaster.showError(errorMsg);
    } else {
      this.$toaster.showError('register.registration-error', true);
    }
  }

  private mapToMobileAppRequest(): MobileAppRegistrationRequest {
    const { userInfo } = this;
    return {
      email: userInfo.email?.trim(),
      password: userInfo.password,
      name: userInfo.name?.trim(),
      surname: userInfo.surname?.trim(),
      language: userInfo.language,
      privacy: userInfo.privacy,
      merchantId: userInfo.merchantId,
      mobileHash: userInfo.mobileHash ?? '',
      privacy1: userInfo.privacy1,
      privacy2: userInfo.privacy2,
      privacy3: userInfo.privacy3,
    };
  }
}
